import React from "react";
import { ContactInfo } from "model";
import { DisabledPrompt, ModalOpen, useStateManager, useBCMSContext } from "bcms-react";

import UINotifiSecondaryButton from "components/shared/notifi/UINotifiSecondaryButton";
import { SVGAsset } from "res";

import "./styles.css";
import "./mobile.css";

/**
 * @function DeliveryMethodGroup
 * @param {STRING} groupTitle - Sets the title of the group
 * @param {STRING} className - Allows us to set a className for the group
 * @param {React.Component} children - Passes through any components into the delivery method group
 * @return {React.Component}
 * @exported true
 * @description This is the container that holds the ContactRows for each delivery method.
 * @example
 * <DeliveryMethodGroup>
 *  <ContactRow icon="SquareMessageBubbleIcon" contact="johndoe@mail.com" className="some-class" />
 * </DeliveryMethodGroup>
 */
export default function DeliveryMethodGroup(props) {
    const groupTitle = props.groupTitle ?? "[groupTitle]";
    const groupType = props.groupType ?? "[groupType]";
    const className = props.className ?? "[className]";
    const dataPointHeaderTitle = props.dataPointHeaderTitle ?? "[dataPoint]";
    const dataPointHeaderAddButton = props.dataPointHeaderAddButton ?? "[dataPoint]";

    const { refresh } = useStateManager();
    const getData = type => ({ workflowType: type, refresh });

    return (
        <div className={ `delivery-method-group ${ className }` }>
            <div className="flex-container flex-width align-center group-title-container">
                <p className="group-title flex-width">
                    <span data-point={ dataPointHeaderTitle }>{groupTitle}</span>
                </p>
                {/*<DisabledPrompt>*/}
                {groupType === "phone" &&
                    <ModalOpen id="AddContactMethodModal" data={ getData("addPhoneWorkflow") }>
                        <div className="flex-container align-center set-width clickable" data-point={ dataPointHeaderAddButton }>
                            <SVGAsset id="PlusIcon" className="inline-text-icon left-icon add-icon" />
                            <p>Add</p>
                        </div>
                    </ModalOpen>
                }
                {groupType === "email" &&
                    <ModalOpen id="AddContactMethodModal" data={ getData("addEmailWorkflow") }>
                        <div className="flex-container align-center set-width clickable" data-point={ dataPointHeaderAddButton }>
                            <SVGAsset id="PlusIcon" className="inline-text-icon left-icon add-icon" />
                            <p>Add</p>
                        </div>
                    </ModalOpen>
                }
                {/*</DisabledPrompt>*/}
            </div>
            
            {props.children}
        </div>
    );
}

/**
 * @function ContactRow
 * @param {STRING} icon - The icon indicates what type of delivery method is being used.
 * @param {STRING} contact - The contact info used to send the notification to
 * @param {STRING} contactMethod - The contact method the user set up
 * @param {React.Component} className - Allows us to set a className for the contact row
 * @return {React.Component}
 * @exported true
 * @description The ContactRows show each contact that is set up for each delivery method.
 * @example The following is called in a loop:
 *  <ContactRow key={contactInfo.id} contactInfo={contactInfo} setAllContactInfo={ setAllContactInfo } allContactInfo={ allContactInfo } />
 */
export function ContactRow(props) {
    const { setPrimary, refresh } = useStateManager();
    const globalController = useBCMSContext();

    const contactInfo = ContactInfo.initAll();
    const info = props.contactInfo ?? ContactInfo.firstInstanceOf();
    const className = props.className ?? "";
    const icon = ContactInfo.getContactInfoIcon(info);

    const isPhone = info.type === "phone";   
    const isEmail = info.type === "email"; 
    const isAlert = info.type === "alert";

    const phoneInfo = ContactInfo.getContactInfoSortedByPrimary(contactInfo, "phone");
    const emailInfo = ContactInfo.getContactInfoSortedByPrimary(contactInfo, "email");

    const isUpdated = contactInfo.filter(contact => {
        return contact.userUpdatedPrimaryStatus === true && contact.id === info.id;
    }, []).length > 0;

    const onClick = evt => setPrimary(globalController, info);

    const type = `${ info.type.charAt(0).toUpperCase() }${ info.type.slice(1) }`;
    const newEmailDataPoint = `${ type }_${ info.id }`;
    const primaryButtonDataPoint = `${ type }_Primary_${ info.id }`;
    const setAsPrimaryButtonDataPoint = `${ type }_SetAsPrimary_${ info.id }`;
    const deleteButtonDataPoint = `${ type }_Delete_${ info.id }`;

    return (
        <div className={ `contact-row ${ className }` }>
            <div className="flex-container flex-width align-center">
                <div className="set-width contact-icon-container">
                    <SVGAsset id={ icon } />
                </div>

                <div className="flex-container flex-width flex-vertical">
                    <div className="flex-container flex-horizontal align-center">
                        <div className="set-width no-device" />

                        <p className="flex-width">
                            <span data-point={ newEmailDataPoint }>{ info.value }</span>
                        </p>

                        {
                            (info.isConfirmed === "true" && info.isPrimary === "true" && info.type !== "alert") &&
                            <div className="flex-width primary-container">
                                <div className="flex-container flex-width">
                                    <div className="set-width primary">
                                        <p data-point={ primaryButtonDataPoint }>
                                            <b>Active</b>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        }

                        {
                            (info.isConfirmed === "false" && info.isPrimary === "false" && info.type !== "alert") &&
                            <div className="flex-width not-confirmed-container">
                                <div className="flex-container flex-width">
                                    <div className="set-width not-confirmed">
                                        <p>
                                            <b>Not Confirmed</b>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        }

                        {
                            (info.isConfirmed === "true" && info.isPrimary === "false" && info.type !== "alert") &&
                            <div className="flex-width primary-container">
                                <div className="flex-container flex-width">
                                    <div className="set-width primary">
                                        <p data-point={ primaryButtonDataPoint }>
                                            <b>Active</b>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>

                    {
                        isUpdated === true &&
                        <div className="set-successfully">
                            <p>{ info.type === "phone" ? "Primary Phone Number " : "Primary Email " } set successfully.</p>
                        </div>
                    }
                </div>

                {   
                    (isPhone && phoneInfo.length > 1) &&
                    <ModalOpen id={ isPhone ? "DeletePhoneModal" : "DeleteEmailModal"} data={ { info, refresh } }>
                        <SVGAsset className="icon set-width close clickable" id="CloseX" data-point={ deleteButtonDataPoint } />
                    </ModalOpen>
                }

                {
                    (isPhone && phoneInfo.length <= 1) &&
                    <div>
                        <DisabledPrompt>
                            <SVGAsset className="icon set-width close clickable" id="CloseX" data-point={ deleteButtonDataPoint } />
                        </DisabledPrompt>
                    </div>
                }

                {
                    (isEmail && emailInfo.length > 1) &&
                    <ModalOpen id={ isPhone ? "DeletePhoneModal" : "DeleteEmailModal"} data={ { info, refresh } }>
                        <SVGAsset className="icon set-width close clickable" id="CloseX" data-point={ deleteButtonDataPoint } />
                    </ModalOpen>
                }

                {
                    (isEmail && emailInfo.length <= 1) &&
                    <div>
                        <DisabledPrompt>
                            <SVGAsset className="icon set-width close clickable" id="CloseX" data-point={ deleteButtonDataPoint } />
                        </DisabledPrompt>
                    </div>
                }

                {
                    isAlert &&
                    <div>
                        <DisabledPrompt>
                            <SVGAsset className="icon set-width close clickable" id="CloseX" data-point={ deleteButtonDataPoint } />
                        </DisabledPrompt>
                    </div>
                }
            </div>
        </div>
    );
}

/**
 * @function ContactRowNoDevice
 * @param {STRING} icon - The icon indicates what type of delivery method is being used.
 * @param {React.Component} className - Allows us to set a className for the contact row
 * @return {React.Component}
 * @exported true
 * @description The ContactRows show each contact that is set up for each delivery method.
 * @example
 * <ContactRowNoDevice icon="SquareMessageBubbleIcon" className="some-class" />
 */
export function ContactRowNoDevice(props) {
    const icon = props.icon ?? "SilentBellIcon";
    const className = props.className ?? "";
  
    return (
      <div className={ `contact-row ${ className }` }>
        <div className="flex-container flex-width align-center">
          <div className="set-width contact-icon-container">
            <SVGAsset id={icon} />
          </div>
          <p className="flex-width">No devices registered.</p>
          {/*<DisabledPrompt>
            <SVGAsset className="icon set-width close clickable" id="CloseX" />
          </DisabledPrompt>*/}
        </div>
      </div>
    );
  }
  
  /**
   * @function NotificationBanner
   * @return {React.Component}
   * @exported true
   * @description This is a banner that shows under the header when there are additional contact methods available for the user to utilize.
   * @example
   * <NotificationBanner />
   */
  export function NotificationBanner(props) {
    const { refresh } = useStateManager();
    const data = { workflowType: "confirmNowWorkflow", refresh };

    return(
      <div className="notification-banner">
        <div className="flex-container flex-horizontal align-center">
          <SVGAsset id="InfoIcon" className="inline-text-icon left-icon set-width" />
          <p className="flex-width">Additional contact methods are available</p>
          {/*<DisabledPrompt>*/}
          <ModalOpen id="AddContactMethodModal" data={ data } className="set-width">
            <UINotifiSecondaryButton data-point="ConfirmNow" icon="ConfirmIcon" text="Confirm Now" className="clickable" />
          </ModalOpen>
          {/*</DisabledPrompt>*/}
        </div>
      </div>
    );
  }
  